/**
 * JsonEditor —— 零依赖的轻量 JSON 编辑器（行号 + 语法高亮 + 实时校验）。
 *
 * 用在「系统参数运维」这类 JSON 配置项上，替代裸 Input.TextArea：
 * 原来格式写错只能在点保存时弹一句 message，错在第几行全靠人工数。
 *
 * ── 实现方式：经典 overlay 三层结构 ──────────────────────────────
 *   .je-gutter   行号列（overflow:hidden，靠 JS 同步 scrollTop）
 *   .je-pre      高亮层，渲染 tokenize 后的 HTML（真正看得见的文字）
 *   .je-textarea 输入层，盖在最上面，color:transparent + caret-color 可见
 *
 * ⚠️ 后来改样式的人务必注意（本组件最容易出 bug 的地方）：
 *   .je-pre 与 .je-textarea 的这些属性必须**逐项一致**，差一个像素文字就会错位，
 *   而且错位是渐进的 —— 第 1 行看着没事，第 40 行光标已经飘到别的字上：
 *     font-family / font-size / line-height / letter-spacing /
 *     padding / border-width / white-space / word-break / tab-size /
 *     overflow + 滚动条占位宽度
 *   为此把这些值统一收敛到本文件的 METRICS 常量，通过 CSS 变量下发给 less，
 *   less 里只允许 var(--je-*) 引用，不允许再写死数值。
 *   同理 .je-gutter 的 line-height / padding-top 也必须跟着走，否则行号对不上行。
 *
 *   清单最后一项（overflow + 滚动条占位）是最容易漏的一条：滚动条只要在一层占位、
 *   另一层不占，那一层的 clientWidth / clientHeight 就少一条滚动条，
 *   maxScrollLeft / maxScrollTop 跟着差同样多，而 handleScroll 是原样赋值的，
 *   于是被钳住 —— 横向拖到最右端时高亮文字比光标偏左 8px，纵向滚到底时整层偏高
 *   约 0.4 行、行号对不上代码行。所以两层一律 overflow:scroll 恒定预留通道，
 *   滚动条规格取同一个 var(--je-scrollbar)；gutter 只跟纵向，用 padding-bottom
 *   补等高的尾部留白。完整推导见 index.less 里 .je-pre/.je-textarea 的注释。
 *
 * 另外：textarea 用 wrap="off" + white-space:pre，即「不软换行、横向滚动」。
 * 这是刻意的 —— 一旦允许软换行，一个逻辑行会占多个视觉行，左侧行号就再也对不齐了。
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type UIEvent,
} from 'react';
import { Button, Tooltip } from 'antd';
import {
  AlignLeftOutlined,
  CheckOutlined,
  ColumnWidthOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import './index.less';

export interface JsonEditorProps {
  /** 受控值：JSON 文本 */
  value: string;
  onChange: (next: string) => void;
  /** JSON 解析成功之后跑的语义校验，返回中文错误文案或 null */
  validate?: (parsed: unknown, raw: string) => string | null;
  placeholder?: string;
  /** 编辑区最小 / 最大可见行数，默认 14 / 34 */
  minRows?: number;
  maxRows?: number;
  /** 校验结果变化时回调（null = 合法）。父组件据此 disable 保存按钮 */
  onValidityChange?: (error: string | null) => void;
  /** Cmd/Ctrl + S 触发 */
  onSave?: () => void;
  readOnly?: boolean;
  /** 工具条右侧塞入的额外操作（如「派生」按钮） */
  extraActions?: ReactNode;
}

/**
 * 两层 overlay 的共用度量。改这里就等于同时改了 pre / textarea / 行号列，
 * 不会出现「只改了一层」的经典错位事故。单位统一 px。
 */
const METRICS = {
  fontSize: 13,
  lineHeight: 20,
  paddingY: 10,
  paddingX: 12,
  /** 行号列宽度基数：不足 3 位数按 3 位算，避免小文件行号栏太窄来回抖 */
  gutterCharWidth: 8,
  /**
   * 滚动条通道宽度。两层 overlay 都用 overflow:scroll 恒定预留横竖两条通道
   * （为什么这么选见 index.less），所以底部那条横向通道要计进编辑区高度，
   * 否则正好 minRows 行的内容会被通道压掉最后半行。
   * 经 --je-scrollbar 下发给 less，两处用的是同一个数。
   */
  scrollbarSize: 8,
} as const;

/** 一次 Tab 的缩进宽度，与「格式化」输出的 2 空格缩进保持一致 */
const INDENT = '  ';

/** 输入到重新解析的 debounce；再短会在连打时空转，再长用户会觉得状态灯迟钝 */
const VALIDATE_DELAY = 180;

const DEFAULT_MIN_ROWS = 14;
const DEFAULT_MAX_ROWS = 34;

/* ────────────────────────────── 语法高亮 ────────────────────────────── */

const HTML_ESCAPE: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;' };

/**
 * 「已经过 HTML 转义、可以安全交给 innerHTML」的字符串。
 *
 * 运行时它就是 string，类型系统挡不住误用 —— 起这个别名是为了把原本只靠一行注释
 * 维持的不变式写进签名里，让下一个改这里的人看见约束：
 *   1. 任何外部文本（用户输入的 JSON）拼进 SafeHtml 之前必须先过 escapeHtml；
 *   2. escapeHtml 只转义 & < >，**不转义引号**。当前所有拼接都发生在标签的文本
 *      位置上，所以是安全的；一旦将来给 token 加 HTML 属性（比如
 *      `title="${matched}"`），就必须额外转义 " 与 '，否则 JSON 里一个双引号就能
 *      逃出属性值、把整段高亮变成可注入面。
 */
type SafeHtml = string;

/**
 * 高亮层是 innerHTML 写进去的，所以在 tokenize **之前**必须先转义 & < >。
 * 否则 JSON 里出现 `"<img onerror=...>"` 就直接变成真 DOM 节点（既破版又是注入面）。
 * 先转义再分词是安全的：转义只动 & < >，不动引号，字符串 token 的正则照样成立。
 */
const escapeHtml = (text: string): SafeHtml => text.replace(/[&<>]/g, (c) => HTML_ESCAPE[c]);

/**
 * 单次扫描的分词正则（顺序即优先级：字符串永远先吃掉，避免把字符串里的数字/关键字染色）。
 *   group1 = 完整字符串字面量；group2 = 紧跟其后的「空白 + 冒号」（有它说明这是对象 key）
 * 其余分支靠首字符回判，省掉额外捕获组 —— 几百行 JSON 也只走一遍 replace，O(n)。
 */
const TOKEN_RE =
  /("(?:\\.|[^"\\])*")(\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|[{}[\],:]/g;

/** 返回 SafeHtml：入参先过 escapeHtml，之后拼进来的只有本函数自己写死的标签 */
const highlightLine = (line: string): SafeHtml =>
  escapeHtml(line).replace(TOKEN_RE, (matched: string, str?: string, colon?: string) => {
    if (str !== undefined) {
      // 后面跟冒号 → 对象 key；否则是字符串值
      if (colon) {
        return `<span class="je-t-key">${str}</span><span class="je-t-punc">${colon}</span>`;
      }
      return `<span class="je-t-str">${str}</span>`;
    }
    if (matched === 'true' || matched === 'false' || matched === 'null') {
      return `<span class="je-t-lit">${matched}</span>`;
    }
    // 按首字符回判数字：注意不能用 matched >= '0' 这类字符串比较，
    // 那是按字典序比的，'9.5e10' <= '9' 会得出 false，多位小数直接漏染色
    const head = matched.charCodeAt(0);
    if (head === 45 /* - */ || (head >= 48 && head <= 57) /* 0-9 */) {
      return `<span class="je-t-num">${matched}</span>`;
    }
    return `<span class="je-t-punc">${matched}</span>`;
  });

/* ────────────────────────────── 校验 ────────────────────────────── */

interface ValidateResult {
  /** null = 合法 */
  error: string | null;
  /** 出错行（1-based），拿不到就是 null */
  errorLine: number | null;
  /** 合法时的概要文案，如「3 个顶层键」 */
  summary: string;
}

/** 把字符偏移换算成「第几行第几列」（均 1-based） */
const offsetToLineCol = (raw: string, offset: number): { line: number; column: number } => {
  const safe = Math.max(0, Math.min(offset, raw.length));
  let line = 1;
  let lastBreak = -1;
  for (let i = 0; i < safe; i += 1) {
    if (raw.charCodeAt(i) === 10 /* \n */) {
      line += 1;
      lastBreak = i;
    }
  }
  return { line, column: safe - lastBreak };
};

/**
 * 从 SyntaxError.message 里抠定位信息。各引擎格式不统一，能拿到什么算什么：
 *   老 V8：   Unexpected token } in JSON at position 42
 *   新 V8：   Expected ',' or '}' after property value in JSON at position 42 (line 3 column 5)
 *   Firefox： JSON.parse: expected property name or '}' at line 3 column 5 of the JSON data
 *   Safari：  JSON Parse error: Expected '}'（压根没有定位，走降级）
 *   无定位：  Unexpected end of JSON input
 * 拿不到就优雅降级 —— 只回显原始 message，绝不瞎猜行号误导人。
 */
const describeSyntaxError = (err: SyntaxError, raw: string): { text: string; line: number | null } => {
  const message = err.message || 'JSON 解析失败';
  // 去掉引擎自带的定位前后缀，剩下的才是「原因」。
  // 尾巴必须剥干净：下面我们自己会拼中文的「第 M 行第 C 列：」，
  // Firefox 那截 `at line 3 column 5 of the JSON data` 留着就是同一个位置说两遍。
  const reason = message
    .replace(/\s*in JSON at position[\s\S]*$/i, '')
    .replace(/\s*at position[\s\S]*$/i, '')
    .replace(/\s*at line\s+\d+\s+column\s+\d+[\s\S]*$/i, '') // Firefox 定位尾巴
    .replace(/^JSON\.parse:\s*/i, '') // Firefox 前缀，跟中文前缀并排读着啰嗦
    .trim();
  // 注意：上面只加工 reason，下面两个定位正则仍然跑在原始 message 上，
  // 所以 Safari 那种「没有任何定位」的 message 依旧原样回显（line 为 null）。

  const lineCol = /line\s+(\d+)\s+column\s+(\d+)/i.exec(message);
  if (lineCol) {
    const line = Number(lineCol[1]);
    return { text: `第 ${line} 行第 ${Number(lineCol[2])} 列：${reason || message}`, line };
  }

  const posMatch = /position\s+(\d+)/i.exec(message);
  if (posMatch) {
    const { line, column } = offsetToLineCol(raw, Number(posMatch[1]));
    return { text: `第 ${line} 行第 ${column} 列：${reason || message}`, line };
  }

  return { text: reason || message, line: null };
};

const summarize = (parsed: unknown): string => {
  if (Array.isArray(parsed)) return `顶层数组 · ${parsed.length} 个元素`;
  if (parsed !== null && typeof parsed === 'object') {
    return `${Object.keys(parsed as Record<string, unknown>).length} 个顶层键`;
  }
  return '顶层为标量值';
};

const runValidate = (
  raw: string,
  validate?: (parsed: unknown, rawText: string) => string | null
): ValidateResult => {
  if (!raw.trim()) {
    return { error: '内容为空，请输入 JSON', errorLine: null, summary: '' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    const { text, line } = describeSyntaxError(err as SyntaxError, raw);
    return { error: text, errorLine: line, summary: '' };
  }
  // 语法过了才谈语义：把 parsed 交给业务方自己的规则
  const semantic = validate ? validate(parsed, raw) : null;
  return { error: semantic, errorLine: null, summary: summarize(parsed) };
};

/* ────────────────────────────── 组件 ────────────────────────────── */

export default function JsonEditor({
  value,
  onChange,
  validate,
  placeholder,
  minRows = DEFAULT_MIN_ROWS,
  maxRows = DEFAULT_MAX_ROWS,
  onValidityChange,
  onSave,
  readOnly = false,
  extraActions,
}: JsonEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);

  const [result, setResult] = useState<ValidateResult>(() => runValidate(value, validate));
  const [copied, setCopied] = useState(false);

  // validate / onSave 常以内联箭头函数传入，每次渲染都是新引用。
  // 放进 ref 而不是 effect 依赖，避免 debounce 定时器被无脑重置成永远跑不完。
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  // 上一次上报给父组件的校验结果。既用来去重（见下面的上报 effect），
  // 也用来判断「当前对外是不是处于非法态」——非法态下的校验不能再 debounce。
  const lastReportedRef = useRef<string | null | undefined>(undefined);

  /* ── 实时校验：debounce 解析 ── */
  const firstRunRef = useRef(true);
  useEffect(() => {
    // debounce 是不对称的，两个方向的代价不一样：
    //   合法 → 非法：延迟是**好事**，用户删一半括号的途中不该立刻闪红；
    //                而且父组件 handleSave 会同步重校验，"看着能点其实非法" 拦得住。
    //   非法 → 合法：延迟是**坏事**，父组件的保存按钮还 disabled 着，
    //                用户刚补完括号去点，那 180ms 里点了没反应，像卡死。
    // 所以只在「当前对外已是非法态」时取消 debounce 立刻重算：此时状态灯本来就是红的，
    // 高频重算既不会闪，还能让补全的那一刻马上解禁保存。
    const blocked = lastReportedRef.current != null;
    const delay = firstRunRef.current || blocked ? 0 : VALIDATE_DELAY;
    firstRunRef.current = false;
    if (delay === 0) {
      // 同步算完，连一帧 setTimeout(0) 的空窗都不留
      setResult(runValidate(value, validateRef.current));
      return;
    }
    const timer = window.setTimeout(() => {
      setResult(runValidate(value, validateRef.current));
    }, delay);
    return () => window.clearTimeout(timer);
  }, [value]);

  /* ── 校验结果变化才向上报 ── */
  useEffect(() => {
    // 用 ref 记住上一次上报的值：同样的结果反复回调会把父组件推进无限 re-render
    if (lastReportedRef.current === result.error) return;
    lastReportedRef.current = result.error;
    onValidityChange?.(result.error);
    // onValidityChange 常是内联函数，不进依赖，否则每次父渲染都重报一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.error]);

  /* ── 高亮：value 变才重新分词；错误行变只重拼字符串，不重复转义 ── */
  const lineHtmls = useMemo(() => value.split('\n').map(highlightLine), [value]);

  const highlightHtml = useMemo(() => {
    const { errorLine } = result;
    let out = '';
    for (let i = 0; i < lineHtmls.length; i += 1) {
      const cls = errorLine === i + 1 ? 'je-line je-line-error' : 'je-line';
      // 空行塞一个 &#8203;（零宽空格）撑出行高，否则空 div 高度塌成 0，后面所有行都会与行号错位
      out += `<div class="${cls}">${lineHtmls[i] || '&#8203;'}</div>`;
    }
    return out;
  }, [lineHtmls, result.errorLine]);

  const lineCount = lineHtmls.length;

  const gutterHtml = useMemo(() => {
    const { errorLine } = result;
    let out = '';
    for (let i = 1; i <= lineCount; i += 1) {
      out += `<div class="${errorLine === i ? 'je-ln je-ln-error' : 'je-ln'}">${i}</div>`;
    }
    return out;
  }, [lineCount, result.errorLine]);

  /* ── 高度：按 minRows/maxRows 夹逼，超出走内部滚动，绝不把页面撑到几千像素 ── */
  const bodyStyle = useMemo<CSSProperties>(() => {
    // 两层 overlay 恒定预留横向滚动条通道（见文件头），底部那条会吃掉可视高度，
    // 所以 chrome 要把它算进去，否则 minRows 行的内容最后一行被通道压掉半截
    const chrome = METRICS.paddingY * 2 + METRICS.scrollbarSize;
    const content = lineCount * METRICS.lineHeight + chrome;
    const min = minRows * METRICS.lineHeight + chrome;
    const max = maxRows * METRICS.lineHeight + chrome;
    return { height: Math.min(Math.max(content, min), max) };
  }, [lineCount, minRows, maxRows]);

  // 行号列宽度随位数增长，最少按 3 位留白，避免 99→100 时整块编辑区横跳
  const gutterWidth = Math.max(3, String(lineCount).length) * METRICS.gutterCharWidth + 16;

  /** METRICS 通过 CSS 变量下发，保证 less 里三层用的是同一组数值 */
  const rootStyle = useMemo<CSSProperties>(
    () =>
      ({
        '--je-font-size': `${METRICS.fontSize}px`,
        '--je-line-height': `${METRICS.lineHeight}px`,
        '--je-pad-y': `${METRICS.paddingY}px`,
        '--je-pad-x': `${METRICS.paddingX}px`,
        '--je-gutter-width': `${gutterWidth}px`,
        '--je-scrollbar': `${METRICS.scrollbarSize}px`,
      }) as CSSProperties,
    [gutterWidth]
  );

  /* ── 滚动同步：textarea 是唯一的滚动源，pre 和行号列被动跟随 ── */
  // 直接原样赋值（不做任何比例换算）的前提是：两层的 scrollWidth/scrollHeight 与
  // clientWidth/clientHeight 完全相同 —— 靠 less 里两层同规格的 overflow:scroll +
  // 同宽滚动条通道保证。改那边的 overflow 或滚动条尺寸，这里就会悄悄错位。
  const handleScroll = useCallback((e: UIEvent<HTMLTextAreaElement>) => {
    const { scrollTop, scrollLeft } = e.currentTarget;
    if (preRef.current) {
      preRef.current.scrollTop = scrollTop;
      preRef.current.scrollLeft = scrollLeft;
    }
    // 行号只跟纵向，横向永远贴左
    if (gutterRef.current) gutterRef.current.scrollTop = scrollTop;
  }, []);

  /* ── 受控编辑后恢复光标 ── */
  // 受控组件里 onChange 之后 DOM value 要等下一次渲染才更新，此时浏览器会把
  // 光标顶到末尾。所以先把目标选区记在 ref，渲染提交后用 layout effect 补回去，
  // 否则每敲一次 Tab 光标就跳到文件尾。
  const pendingSelectionRef = useRef<[number, number] | null>(null);
  useLayoutEffect(() => {
    const pending = pendingSelectionRef.current;
    if (!pending || !textareaRef.current) return;
    pendingSelectionRef.current = null;
    textareaRef.current.setSelectionRange(pending[0], pending[1]);
  });

  const applyEdit = useCallback(
    (next: string, selStart: number, selEnd: number) => {
      pendingSelectionRef.current = [selStart, selEnd];
      onChange(next);
    },
    [onChange]
  );

  /** Tab / Shift+Tab：单光标插入或删除缩进，有选区则整段行批量处理 */
  const handleIndent = useCallback(
    (outdent: boolean) => {
      const ta = textareaRef.current;
      if (!ta) return;
      const { selectionStart: start, selectionEnd: end } = ta;
      const raw = value;

      // 无选区：只动光标所在位置 / 所在行
      if (start === end) {
        if (!outdent) {
          const next = raw.slice(0, start) + INDENT + raw.slice(start);
          applyEdit(next, start + INDENT.length, start + INDENT.length);
          return;
        }
        const lineStart = raw.lastIndexOf('\n', start - 1) + 1;
        const leading = /^[ \t]{1,2}/.exec(raw.slice(lineStart, lineStart + 2));
        if (!leading) return;
        const removed = leading[0].length;
        const next = raw.slice(0, lineStart) + raw.slice(lineStart + removed);
        const caret = Math.max(lineStart, start - removed);
        applyEdit(next, caret, caret);
        return;
      }

      // 有选区：把选区覆盖到的每一整行都缩进 / 反缩进
      const blockStart = raw.lastIndexOf('\n', start - 1) + 1;
      // 选区正好停在行首换行处时，不要把下一行也算进来（用户视觉上没选中它）
      const probe = raw[end - 1] === '\n' ? end - 1 : end;
      const nextBreak = raw.indexOf('\n', probe);
      const blockEnd = nextBreak === -1 ? raw.length : nextBreak;

      const lines = raw.slice(blockStart, blockEnd).split('\n');
      let firstDelta = 0;
      let totalDelta = 0;
      const rebuilt = lines.map((line, idx) => {
        let delta = 0;
        let out = line;
        if (outdent) {
          const leading = /^[ \t]{1,2}/.exec(line.slice(0, 2));
          if (leading) {
            delta = -leading[0].length;
            out = line.slice(leading[0].length);
          }
        } else if (line.length > 0) {
          // 空行不加缩进，免得留下一串看不见的尾随空格
          delta = INDENT.length;
          out = INDENT + line;
        }
        if (idx === 0) firstDelta = delta;
        totalDelta += delta;
        return out;
      });

      const next = raw.slice(0, blockStart) + rebuilt.join('\n') + raw.slice(blockEnd);
      applyEdit(
        next,
        Math.max(blockStart, start + firstDelta),
        Math.max(blockStart, end + totalDelta)
      );
    },
    [applyEdit, value]
  );

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
      // Cmd/Ctrl + S：吃掉浏览器的「保存网页」对话框
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        if (!onSaveRef.current) return;
        e.preventDefault();
        // 快捷键必须和保存按钮同一道门禁：按钮在校验不过时是 disabled 的，
        // 这里若无条件调用，用户在非法态按 Ctrl+S 就绕过了拦截直接发请求。
        // 用**同步**重算而不是读 result（debounce 期间 result 还是上一帧的结论）。
        if (runValidate(value, validateRef.current).error != null) return;
        onSaveRef.current();
        return;
      }
      if (readOnly) return;
      if (e.key === 'Tab') {
        // 只读态才让 Tab 切焦点；编辑态一律当缩进用
        e.preventDefault();
        handleIndent(e.shiftKey);
      }
    },
    [handleIndent, readOnly, value]
  );

  /* ── 工具条动作 ── */
  const reformat = useCallback(
    (indent: number) => {
      try {
        const next = JSON.stringify(JSON.parse(value), null, indent);
        // 格式化后行结构整体变了，光标归到开头比留在原偏移更符合直觉
        applyEdit(next, 0, 0);
      } catch {
        /* 按钮在非法态是 disabled 的，正常走不到这里 */
      }
    },
    [applyEdit, value]
  );

  // 卸载标记：handleCopy 有 await，用它避免在已卸载的组件上再 setCopied
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // 非 https / 老浏览器没有 clipboard API，降级到 execCommand
      const scratch = document.createElement('textarea');
      scratch.value = value;
      scratch.style.position = 'fixed';
      scratch.style.opacity = '0';
      document.body.appendChild(scratch);
      scratch.select();
      try {
        document.execCommand('copy');
      } catch {
        /* 两条路都失败就静默放弃，不打扰用户 */
      }
      document.body.removeChild(scratch);
    }
    if (mountedRef.current) setCopied(true);
  }, [value]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const valid = result.error === null;
  const showPlaceholder = value.length === 0 && !!placeholder;

  return (
    <div className="json-editor" style={rootStyle}>
      <div className="je-toolbar">
        <div className={`je-status ${valid ? 'je-status-ok' : 'je-status-bad'}`}>
          <span className="je-dot" />
          <Tooltip title={valid ? '' : result.error} placement="topLeft">
            <span className="je-status-text">
              {valid ? `JSON 合法${result.summary ? ` · ${result.summary}` : ''}` : result.error}
            </span>
          </Tooltip>
        </div>
        <div className="je-actions">
          {!readOnly && (
            <>
              <Tooltip title="格式化（2 空格缩进）">
                <Button
                  size="small"
                  type="text"
                  disabled={!valid}
                  icon={<AlignLeftOutlined />}
                  onClick={() => reformat(2)}
                >
                  格式化
                </Button>
              </Tooltip>
              <Tooltip title="压缩为单行">
                <Button
                  size="small"
                  type="text"
                  disabled={!valid}
                  icon={<ColumnWidthOutlined />}
                  onClick={() => reformat(0)}
                >
                  压缩
                </Button>
              </Tooltip>
            </>
          )}
          <Tooltip title="复制全文">
            <Button
              size="small"
              type="text"
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
              onClick={handleCopy}
            >
              {copied ? '已复制' : '复制'}
            </Button>
          </Tooltip>
          {extraActions}
        </div>
      </div>

      <div className={`je-body ${readOnly ? 'je-body-readonly' : ''}`} style={bodyStyle}>
        {/* 行号列：纵向被动跟随 textarea */}
        <div className="je-gutter" ref={gutterRef}>
          <div className="je-gutter-inner" dangerouslySetInnerHTML={{ __html: gutterHtml }} />
        </div>

        <div className="je-code">
          {/* 高亮层：内容来自 highlightLine，已做 HTML 转义 */}
          <pre className="je-pre" ref={preRef} aria-hidden="true">
            <code
              className="je-pre-inner"
              dangerouslySetInnerHTML={{ __html: highlightHtml }}
            />
          </pre>
          {/* 占位提示画在高亮层之上、输入层之下，避免 textarea 原生 placeholder 被 transparent 文字色带走 */}
          {showPlaceholder && <div className="je-placeholder">{placeholder}</div>}
          <textarea
            ref={textareaRef}
            className="je-textarea"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onScroll={handleScroll}
            onKeyDown={handleKeyDown}
            readOnly={readOnly}
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            wrap="off"
          />
        </div>
      </div>
    </div>
  );
}
